<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref } from "vue"
import { useI18n } from "vue-i18n"
import { ServiceError } from "@core/exceptions"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import type { Services } from "@services"
import type { ThresholdPoint } from "@modules/energy-manager/types/alarme"

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    type: "building" | "source"
    itemId: string
    variable: string
    /**
     * Déduit de la longueur du profil (7 = daily, 168 = hourly), aligné legacy `temp`.
     */
    freq: "hourly" | "daily" | null
    /**
     * Cible Teleport (`#id`) pour placer « Sauvegarder / Annuler » sous le graphique parent.
     */
    teleportTarget?: string
    /** Active le bouton sauvegarde (ex. ordre des seuils valide côté parent). */
    canSave?: boolean
    /** Sauvegarde API en cours (désactive les boutons). */
    saving?: boolean
}>()

const emit = defineEmits<{
    "change-points": [payload: ThresholdPoint[]]
    confirm: []
    cancel: []
}>()

// ================================
// COMPOSABLES & SERVICES
// ================================
const services = inject<Services>("services")!
/** Points renvoyés par l’API après un appel réussi (aperçu graphique). */
const lastGeneratedPoints = ref<ThresholdPoint[] | null>(null)
const feedbackMessage = ref("")

const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                autoGenerate: "Générer le profil",
                autoGenerateHint:
                    "Nos algorithmes calculent le profil d'alarme optimal à partir de vos données.",
                errorGenerate: "La génération du profil a échoué.",
                invalidVariableNoData:
                    "Pas assez de données pour cette variable.",
                save: "Sauvegarder",
                cancel: "Annuler",
            },
        },
    },
})

// ================================
// COMPUTED
// ================================
const getCanGenerate = computed(
    () =>
        Boolean(props.itemId) &&
        Boolean(props.variable) &&
        (props.freq === "hourly" || props.freq === "daily")
)

/** Affiche les actions de validation sous le graphique (Teleport ou emplacement local). */
const getShowChartActions = computed(() =>
    Boolean(lastGeneratedPoints.value?.length)
)

const getCanSaveResolved = computed(() => props.canSave !== false)

const getTeleportSelector = computed(() => {
    const raw = props.teleportTarget?.trim()
    return raw && raw.length > 0 ? raw : null
})

// ================================
// INTERACTIONS
// ================================
const {
    fetch: fetchGeneratedProfile,
    isFetching: isGenerateProfileFetching,
    error: generateProfileError,
} = useFetchWrapper(async () => {
    const freq = props.freq
    if (freq !== "hourly" && freq !== "daily") return
    lastGeneratedPoints.value = null
    const { points } =
        props.type === "building"
            ? await services.$getBuildingProfileByData(
                  props.itemId,
                  props.variable,
                  freq
              )
            : await services.$getSourceProfileByData(
                  props.itemId,
                  props.variable,
                  freq
              )
    if (!points?.length) {
        throw new Error("EMPTY_GRAPH")
    }
    lastGeneratedPoints.value = points
})

async function onGenerate() {
    if (!getCanGenerate.value || isGenerateProfileFetching.value) return
    feedbackMessage.value = ""
    await fetchGeneratedProfile()
    const err = generateProfileError.value
    if (err) {
        if (err.message === "EMPTY_GRAPH") {
            feedbackMessage.value = t("alarm.errorGenerate")
        } else if (
            err instanceof ServiceError &&
            err.code === "invalid_variable_no_data"
        ) {
            feedbackMessage.value = t("alarm.invalidVariableNoData")
        } else {
            feedbackMessage.value = t("alarm.errorGenerate")
        }
        return
    }
    const pts = lastGeneratedPoints.value
    if (pts?.length) {
        emit("change-points", pts)
    }
}

function onConfirmGenerated() {
    if (
        !getShowChartActions.value ||
        !getCanSaveResolved.value ||
        props.saving
    ) {
        return
    }
    emit("confirm")
}

function onCancelGenerated() {
    emit("cancel")
}
</script>

<template>
    <div class="alarm-threshold-form-auto-generate flex flex-col gap-3">
        <p class="text-sm text-slate-600">
            {{ t("alarm.autoGenerateHint") }}
        </p>
        <BaseButton
            :loading="isGenerateProfileFetching"
            :disabled="!getCanGenerate || isGenerateProfileFetching"
            @click="onGenerate"
            :label="t('alarm.autoGenerate')"
        />
        <BaseMessage
            v-if="feedbackMessage"
            severity="error"
            :closable="true"
            @close="feedbackMessage = ''"
        >
            {{ feedbackMessage }}
        </BaseMessage>

        <!-- Actions sous le graphique : Teleport vers `#teleportTarget` ou rendu local -->
        <template v-if="getShowChartActions">
            <Teleport v-if="getTeleportSelector" :to="getTeleportSelector">
                <div
                    class="flex flex-wrap justify-center gap-2 border-t border-slate-100 pt-3"
                >
                    <BaseButton
                        type="button"
                        outlined
                        severity="secondary"
                        :disabled="Boolean(saving)"
                        :label="t('alarm.cancel')"
                        @click="onCancelGenerated"
                    />
                    <BaseButton
                        type="button"
                        :loading="Boolean(saving)"
                        :disabled="!getCanSaveResolved || Boolean(saving)"
                        :label="t('alarm.save')"
                        @click="onConfirmGenerated"
                    />
                </div>
            </Teleport>
            <div
                v-else
                class="flex flex-wrap justify-center gap-2 border-t border-slate-100 pt-3"
            >
                <BaseButton
                    type="button"
                    outlined
                    severity="secondary"
                    :disabled="Boolean(saving)"
                    :label="t('alarm.cancel')"
                    @click="onCancelGenerated"
                />
                <BaseButton
                    type="button"
                    :loading="Boolean(saving)"
                    :disabled="!getCanSaveResolved || Boolean(saving)"
                    :label="t('alarm.save')"
                    @click="onConfirmGenerated"
                />
            </div>
        </template>
    </div>
</template>
